import type { JSX } from "react";
import { useRef, useState } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { useControlContext } from "@frontend/contexts";

import { MonthPickerContext } from "./MonthPicker.context";
import type { MonthPickerProps } from "./MonthPicker.types";
import { formatMonth, parseMonth, thisMonth } from "./monthMath";

export function MonthPicker({
  size,
  layout = "default",
  min,
  max,
  locale,
  disabled,
  arrow,
  open,
  defaultOpen,
  onOpenChange,
  modal,
  children,
  value,
  defaultValue,
  onValueChange,
}: MonthPickerProps): JSX.Element {
  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;
  const pointerOffsetRef = useRef(0);
  const actionsRef = useRef<BasePopover.Root.Actions>(null);

  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<string | null>(
    defaultValue ?? null,
  );

  const selected = parseMonth(isControlled ? value : internalValue);

  const [viewYear, setViewYear] = useState(
    () => (selected ?? thisMonth()).year,
  );

  function selectMonth(year: number, month: number) {
    const next = formatMonth({ year, month });

    if (!isControlled) {
      setInternalValue(next);
    }

    onValueChange?.(next);
    actionsRef.current?.close();
  }

  return (
    <MonthPickerContext.Provider
      value={{
        size: resolvedSize ?? "md",
        layout,
        disabled: disabled ?? false,
        arrow: arrow ?? true,
        locale,
        viewYear,
        selected,
        min: parseMonth(min),
        max: parseMonth(max),
        pointerOffsetRef,
        selectMonth,
        goToYear: (delta) => {
          setViewYear((current) => current + delta);
        },
        goToView: (year) => {
          setViewYear(year);
        },
      }}
    >
      <BasePopover.Root
        data-slot="month-picker-root"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={modal}
        actionsRef={actionsRef}
      >
        {children}
      </BasePopover.Root>
    </MonthPickerContext.Provider>
  );
}
